import React, { useEffect, useState } from "react";
import { Volume2, VolumeX, Square } from "lucide-react";
import {
  VOICE_REPLIES_EVENT,
  VOICE_SPEAKING_EVENT,
  requestStopSpeaking,
  setVoiceRepliesEnabled,
  voiceRepliesEnabled,
} from "./VoiceReplySpeaker";

/**
 * VoiceReplyToggle — the in-chat speaker button, next to the mic.
 *
 * Voice IN (the mic, speech-to-text) and voice OUT (replies read aloud)
 * are independent controls (Brad, 2026-08-07: "we need to make sure the
 * little mic and the voice that talks back are separately applicable").
 * The mic never turns speech on; this button is the ONLY front-and-center
 * control for talk-back. It drives the same `voiceReplies` setting as
 * Settings → General, kept in sync via VOICE_REPLIES_EVENT.
 *
 * Three states:
 *   ⏹ (speaking)  — click stops the CURRENT reply mid-sentence; setting
 *                    stays on for the next one (Orca's ESC barge-in).
 *   🔊 (on, idle)  — click turns voice replies off (also silences).
 *   🔇 (off)       — click turns voice replies on.
 */
const VoiceReplyToggle = () => {
  const [enabled, setEnabled] = useState(voiceRepliesEnabled());
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    const onSetting = () => setEnabled(voiceRepliesEnabled());
    const onSpeaking = (e) => setSpeaking(e?.detail?.speaking === true);
    window.addEventListener(VOICE_REPLIES_EVENT, onSetting);
    window.addEventListener(VOICE_SPEAKING_EVENT, onSpeaking);
    // Cross-tab sync (localStorage "storage" fires in OTHER tabs only).
    window.addEventListener("storage", onSetting);
    return () => {
      window.removeEventListener(VOICE_REPLIES_EVENT, onSetting);
      window.removeEventListener(VOICE_SPEAKING_EVENT, onSpeaking);
      window.removeEventListener("storage", onSetting);
    };
  }, []);

  const onClick = () => {
    if (speaking) {
      requestStopSpeaking(); // barge-in: this reply only, setting unchanged
      return;
    }
    setVoiceRepliesEnabled(!enabled);
  };

  const title = speaking
    ? "Stop reading this reply (voice replies stay on)"
    : enabled
      ? "Voice replies ON — click to mute (mic input stays available)"
      : "Voice replies OFF — click to have replies read aloud";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`chat-action-btn voice-reply-toggle ${enabled || speaking ? "is-active" : ""} ${speaking ? "is-speaking" : ""}`}
      aria-label={title}
      aria-pressed={enabled}
      title={title}
    >
      {speaking ? (
        <Square size={16} fill="currentColor" />
      ) : enabled ? (
        <Volume2 size={18} />
      ) : (
        <VolumeX size={18} />
      )}
    </button>
  );
};

export default VoiceReplyToggle;
